/**
 * dog-vaccinations.js
 */

import React from "react";
import { 
  styled,
  Box, 
  Typography, 
  List, 
  ListItem, 
  Link, 
  Chip,
  Divider,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { format, differenceInMonths, differenceInDays } from "date-fns";
import { makeLocation } from 'utils/routing';
import { DogsContext } from "contexts/dogs/dogs-context";
import DrawerContent from 'components/menu-drawer/drawer-content';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DogDrawerMobileHeader from './dog-drawer-mobile-header';
import IconSection from 'components/icon-section';
import Icon from "components/icon";


// TODO: styling from row status chip. Consolidate?
const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  '& .MuiChip-label': {
    display: 'flex',
    alignItems: 'center',
    lineHeight: '14px',
  },
  '& .MuiSvgIcon-root': {
    width: '14px',
    height: '14px',
    marginRight: theme.spacing(0.5),
  },  
}));


function VaccinationDetails(props) {
  const { 
    type,
    vaccinations,
  } = props;
  const now = new Date();
  const mostRecentVac = vaccinations?.find(vac => new Date(vac.dateTime) < now);
  const nextVacDate = mostRecentVac?.dateNextVaccination;

  return (
    <Box m={1.5}>

      {differenceInDays(nextVacDate, now) < 0
        ? <StatusChip
          size='small'
          label={<><Icon type='vaccination-due-status' /> Overdue</>}
          className='chip-error chip-font-error'
        />
        : differenceInMonths(nextVacDate, now) < 1 &&
        <StatusChip
          size='small'
          label={<><Icon type='vaccination-due-status' /> Due Soon</>}
          className='chip-upcoming chip-font-upcoming'
        />
      }

      <Typography variant='subtitle2' key={type.id} sx={{ fontSize: 'sizes.3' }}>
        {type.name}
        <Box 
          component='span' 
          sx={{ 
            fontSize: 'sizes.1',
            fontWeight: 'normal',
            marginLeft: 2,
           }}
        >
          Next due: {
            nextVacDate
              ? format(nextVacDate, 'MM/dd/yy')
              : 'not specified'
          }
        </Box>
      </Typography>
      {vaccinations
        ? <List 
          disablePadding
          sx={{ 
            listStyleType: 'disc',
            listStylePosition: 'inside'
          }}
        >
          {vaccinations.map(vac => 
            <ListItem 
              key={vac.visitId} 
              sx={{ 
                display: 'list-item', 
                padding: 0, 
                paddingTop: 0.5, 
                paddingLeft: 1, 
                fontSize: 13 
              }}
            >
              {format(vac.dateTime, 'MM/dd/yy')}
              {vac.location &&
                <>{' '}at {vac.location}</>
              }
              <Link
                component={RouterLink}
                to={makeLocation(
                  '/records',
                  'vetVisit',
                  'view',
                  { vetVisitId: vac.visitId }
                )}
                sx={{ marginLeft: 2, fontWeight: 500 }}
              >
                VIEW
              </Link>
            </ListItem>
          )}
        </List>
        : <Typography sx={{ fontSize: 13 }}>No recorded vaccinations</Typography>
      }
    </Box>
  );
}


export default function DogVaccinations(props) {
  const {
    dog,
    onClose,
    basePath,
  } = props;
  const { options } = React.useContext(DogsContext);
  const title = 'Vaccination Summary';


  // Group vaccinations by type. Assume that they are already sorted in descending order by datetime.
  const groupedVaccinations = React.useMemo(() => {
    const result = {};
    if (dog) {
      for (const vac of dog.vaccinations) {
        if (!result[vac.vaccinationTypeId]) {
          result[vac.vaccinationTypeId] = [];
        }
        result[vac.vaccinationTypeId].push(vac);
      }
    }
    return result;
  }, [dog]);


  const mobileAppBar = (
    <DogDrawerMobileHeader
      title={title}
      onClose={onClose}
      basePath={basePath}
    />
  );


  return <>
    <DrawerContentBuilder
      mobileAppBar={{ content: mobileAppBar }}
      desktopHeader={{ title }}
      stickyHeader
    >
      <DrawerContent>
        <IconSection iconName='vaccination'>
          <Typography variant='subtitle1'>Core Vaccinations <small>(every 3 years)</small></Typography>
          {options.vaccinationCoreTypes.map((type, idx) =>
            <>
              <VaccinationDetails
                key={type.id}
                type={type}
                vaccinations={groupedVaccinations[type.id]}
              />
              {idx < (options.vaccinationCoreTypes.length - 1) &&
                <Divider sx={{ maxWidth: 200, marginLeft: 1.5 }} />
              }
            </>
          )}
        </IconSection>
        <IconSection iconName='vaccination'>
          <Typography variant='subtitle1'>Other Vaccinations <small>(every year)</small></Typography>
          {options.vaccinationNonCoreTypes.map((type, idx) =>
            <>
              <VaccinationDetails
                key={type.id}
                type={type}
                vaccinations={groupedVaccinations[type.id]}
              />
              {idx < (options.vaccinationNonCoreTypes.length - 1) &&
                <Divider sx={{ maxWidth: 200, marginLeft: 1.5 }} />
              }
            </>
          )}
        </IconSection>
      </DrawerContent>
    </DrawerContentBuilder>
  </>;
}